import { Link } from "react-router-dom";
import { type ContainerStatus, type OwnerRef } from "@k8s-dashboard/contracts";
import { useManagedDeployment, useManagedSecret, useScope } from "@/api/queries";
import { Panel, StatusBadge } from "@/components/primitives";
import { useT } from "@/i18n/I18nProvider";
import { withSearch } from "@/components/drill";

/**
 * Pod 상세의 표·카드 (이슈 #15 · #33)
 * --------------------------------------------------------------------------
 * PodDetail.tsx에서 **그대로 옮겨온** 표현 컴포넌트입니다. 동작·문구가 바뀌지
 * 않았고, Live Logs 탭(ADR 0021)이 붙으면서 화면 파일이 300줄을 넘지 않도록
 * 표현부만 분리했습니다.
 */

export function ContainerTable({ containers }: { containers: ContainerStatus[] }) {
  const { t } = useT("common");
  return (
    /* 스크롤되는 영역은 키보드로도 스크롤할 수 있어야 하고(마우스 휠이 없는 사용자가
       아래쪽을 볼 수 없게 됩니다), 그 접근성 이름은 화면 언어를 따라야 합니다.
       이 파일의 나머지 문구는 아직 이관 대상이 아닙니다. (#13 · ADR 0025) */
    <div className="panel__scroll" tabIndex={0} role="group" aria-label={t("common.list.containers")}>
      <table className="ds-data-table ds-data-table--compact">
        <thead>
          <tr>
            <th style={{ width: 110 }}>이름</th>
            <th style={{ width: 110 }}>상태</th>
            <th className="ds-num" style={{ width: 80 }}>
              재시작
            </th>
            <th style={{ width: 130 }}>Probe</th>
            <th>이미지 / 사유</th>
          </tr>
        </thead>
        <tbody>
          {containers.map((c) => (
            <tr key={c.name}>
              <td className="ds-ident">{c.name}</td>
              <td>
                <StatusBadge
                  severity={c.ready ? "healthy" : c.state === "Waiting" ? "critical" : "warning"}
                  label={c.reason ?? c.state}
                  small
                />
              </td>
              <td className="ds-num">{c.restarts}</td>
              <td className="muted">
                liveness {c.probes.liveness === "passing" ? "정상" : c.probes.liveness === "failing" ? "실패" : "없음"} ·
                readiness{" "}
                {c.probes.readiness === "passing" ? "정상" : c.probes.readiness === "failing" ? "실패" : "없음"}
              </td>
              <td>
                <div className="ds-ident" style={{ maxWidth: 520 }} title={c.imageId}>
                  {c.image}
                </div>
                {c.message && <div className="muted">{c.message}</div>}
                {c.lastTerminated && (
                  <div className="muted">
                    마지막 종료: {c.lastTerminated.reason} (exit {c.lastTerminated.exitCode})
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Secret/Deployment 조회 카드 (#33) ─────────────────────────────────────
   pod 상세에서 연결된 Deployment/Secret을 순수 조회하고, details 버튼으로 각
   관리 탭의 해당 항목으로 이동합니다. 관리 권한이 있을 때만 노출합니다. */

export function ownerDeploymentName(chain?: OwnerRef[]): string {
  return chain?.find((o) => o.kind === "Deployment")?.name ?? "";
}

export function PodManagedCards({
  clusterId,
  namespace,
  search,
  deploymentName,
  secretRefs,
}: {
  clusterId: string;
  namespace: string;
  search: string;
  deploymentName: string;
  secretRefs: string[];
}) {
  const scope = useScope();
  if (!scope.data?.canManageWorkloads) return null;
  const dep = useManagedDeployment(clusterId, namespace, deploymentName, Boolean(deploymentName));
  const secretName = secretRefs[0] ?? "";
  const sec = useManagedSecret(clusterId, namespace, secretName, Boolean(secretName));

  return (
    <div className="grid grid--split">
      <Panel
        title="Deployment"
        subtitle="이 Pod의 상위 Deployment (조회)"
        actions={
          deploymentName ? (
            <Link to={withSearch("/deployments", search, { item: `${namespace}/${deploymentName}` })}>details →</Link>
          ) : undefined
        }
      >
        {!deploymentName ? (
          <p className="muted">Deployment 소유자가 없습니다.</p>
        ) : dep.isLoading ? (
          <p className="muted">불러오는 중…</p>
        ) : dep.data ? (
          <div className="facts">
            <div className="fact">
              <span className="fact__label">이름</span>
              <span className="fact__value ds-ident">{dep.data.name}</span>
            </div>
            <div className="fact">
              <span className="fact__label">Ready</span>
              <span className="fact__value">{dep.data.ready}/{dep.data.desired}</span>
            </div>
            <div className="fact">
              <span className="fact__label">Pod 수</span>
              <span className="fact__value">{dep.data.pods.length}</span>
            </div>
          </div>
        ) : (
          <p className="muted">조회할 수 없습니다.</p>
        )}
      </Panel>

      <Panel
        title="Secret"
        subtitle={secretRefs.length > 1 ? `이 Pod가 참조하는 Secret ${secretRefs.length}개 중 첫 번째` : "이 Pod가 참조하는 Secret (조회)"}
        actions={
          secretName ? (
            <Link to={withSearch("/secrets", search, { item: `${namespace}/${secretName}` })}>details →</Link>
          ) : undefined
        }
      >
        {!secretName ? (
          <p className="muted">참조하는 Secret이 없습니다.</p>
        ) : sec.isLoading ? (
          <p className="muted">불러오는 중…</p>
        ) : sec.data ? (
          <div className="facts">
            <div className="fact">
              <span className="fact__label">이름</span>
              <span className="fact__value ds-ident">{sec.data.name}</span>
            </div>
            <div className="fact">
              <span className="fact__label">타입</span>
              <span className="fact__value">{sec.data.secretType}</span>
            </div>
            <div className="fact">
              <span className="fact__label">키 수</span>
              <span className="fact__value">{Object.keys(sec.data.data).length}</span>
            </div>
            {secretRefs.length > 1 && (
              <div className="fact">
                <span className="fact__label">기타 참조</span>
                <span className="fact__value muted">{secretRefs.slice(1).join(", ")}</span>
              </div>
            )}
          </div>
        ) : (
          <p className="muted">조회할 수 없습니다.</p>
        )}
      </Panel>
    </div>
  );
}
